import { z } from "zod";
import { sequenceSlotSchema } from "./call-sequence.js";
import { notCallableReasonSchema } from "./callability.js";
import { actorViewSchema } from "./people.js";
import { telephonyProviderSchema } from "./telephony.js";
import { idSchema, multiLineTextSchema } from "./text.js";
// Le chiamate della telefonia (piano M4, Task 8, D4, D5, D9, D13). Il browser chiede una chiamata indicando il posto del
// telefono, mai il numero: il numero chiamato e il numero mostrato li decide il server, che crea la riga della chiamata
// prima che il browser componga. Le rotte sono `POST /api/telephony/token`, `POST /api/leads/:leadId/calls`,
// `GET /api/leads/:leadId/calls/open`, `GET /api/calls/:callId`, `POST /api/calls/:callId/abandon`, (Task 10)
// `POST /api/calls/:callId/outcome` e (Task 11a) `GET /api/calls/:callId/recording`.
//
// Gli elenchi qui sotto ripetono quelli del dominio (`@gioia365/domain`: `call-outcomes.ts`, `call-lifecycle.ts`, `recordings.ts`)
// e del database (le sorgenti delle telefonate), che l'interfaccia e i contratti non importano: un test del server li confronta
// voce per voce.
/** Il primo passo dell'esito. */
export const CALL_OUTCOMES = ["no_answer", "answered", "wrong_number"];
/** Il secondo passo, soltanto dopo «Risposta». */
export const ANSWERED_OUTCOMES = ["call_back", "not_interested", "do_not_call", "disco_booked", "demo_booked", "other"];
/** Il dettaglio dell'esito: «Richiama ora», che registra «Nessuna risposta» e richiama subito lo stesso numero. */
export const OUTCOME_DETAILS = ["call_again_now"];
/** Che cosa vede chi guarda la chiamata. */
export const CALL_LIVE_STATES = ["connecting", "ringing", "answered", "ended"];
/** Come è finita una chiamata della telefonia; `not_connected` e `destination_not_allowed` non sono mai arrivate al Lead. */
export const CALL_END_REASONS = ["completed", "busy", "no_answer", "canceled", "failed", "not_connected", "destination_not_allowed"];
/**
 * Gli stati della registrazione (piano M4, D8; `recordings.ts` del dominio): in preparazione, da ascoltare, scaduta, assente,
 * non riuscita. La Timeline li dice con le parole di `REC_TEXT` del mock-up (Task 12).
 */
export const CALL_RECORDING_STATES = ["processing", "available", "expired", "missing", "error"];
/** Da dove viene una telefonata: l'importazione da Close, l'inserimento a mano, la telefonia di Gioia365. */
export const CALL_SOURCES = ["close_import", "manual", "telephony"];
/** Il posto del telefono da chiamare: titolare o secondo titolare, cellulare o fisso. */
export const phoneSlotSchema = z.strictObject({ holder: z.enum(["primary", "second"]), kind: z.enum(["mobile", "landline"]) });
/**
 * `POST /api/leads/:leadId/calls`: il posto del telefono e la chiave di idempotenza del browser. La stessa chiave della
 * stessa persona dà la stessa chiamata (200 invece di 201). `dialerSessionId` (piano M5, D5) è la sessione del Power Dialer da
 * cui parte la chiamata: dev'essere la sessione attiva di chi chiama con questo Lead aperto, e si scrive sulla chiamata.
 */
export const startCallBodySchema = z.strictObject({ phone: phoneSlotSchema, requestId: idSchema, dialerSessionId: idSchema.optional() });
/**
 * Una chiamata della telefonia come la vede chi guarda. `endedReason` è uno dei modi di `CALL_END_REASONS` (gli stessi del
 * controllo del database), oppure `null` finché non è finita. `needsOutcome`: la chiamata è valida, è finita, non ha
 * l'esito, e chi guarda ne è l'autore; soltanto allora la postazione chiede l'esito. `sequenceSlot` (piano M5, Task 11, D9):
 * il suo posto nella sequenza di chiamate del Lead, «tentativo N · chiamata M», `null` senza un posto o per una chiamata non
 * arrivata al Lead; una risposta di prima del 4 ottobre 2026, senza, vale `null`.
 */
export const callQualityViewSchema = z.object({
    ringingAt: z.string().nullable(),
    ringDurationSeconds: z.number().int().nonnegative().nullable(),
    closureQuality: z.enum(["closed_early", "hangup_unmeasured"]).nullable(),
    numberProblem: z.enum(["invalid_number", "unreachable"]).nullable(),
});
export const callViewSchema = z.object({
    id: z.string(),
    leadId: z.string().nullable(),
    direction: z.enum(["outbound", "inbound"]),
    startedAt: z.string(),
    liveState: z.enum(CALL_LIVE_STATES),
    endedReason: z.enum(CALL_END_REASONS).nullable(),
    durationSeconds: z.number().int().nullable(),
    quality: callQualityViewSchema.nullable().optional(),
    outcome: z.enum(CALL_OUTCOMES).nullable(),
    answeredOutcome: z.enum(ANSWERED_OUTCOMES).nullable(),
    author: actorViewSchema,
    needsOutcome: z.boolean(),
    sequenceSlot: sequenceSlotSchema.nullable().default(null),
});
/** La chiamata appena creata (201) o già creata con la stessa chiave (200), con il fornitore con cui il browser compone. */
export const startCallResponseSchema = z.object({ call: callViewSchema, provider: telephonyProviderSchema });
/** `GET /api/calls/:callId` e `POST /api/calls/:callId/abandon`. */
export const callResponseSchema = z.object({ call: callViewSchema });
/**
 * `POST /api/calls/:callId/outcome` (piano M4, Task 10, D7): l'esito in due passi, salvato una volta sola dall'autore della
 * chiamata. `answeredOutcome` c'è soltanto dopo «Risposta», `detail` («Richiama ora») soltanto con «Nessuna risposta», e
 * «Altro» vuole gli appunti (la regola è `validateOutcome` del dominio: un corpo che non la rispetta è `422`). Gli appunti
 * diventano una nota della chiamata. `requestId` è la chiave del browser: lo stesso salvataggio ripetuto risponde come la
 * prima volta; un esito diverso su una chiamata che ne ha già uno è `409 call_outcome_exists`, e resta nella Timeline come
 * esito arrivato tardi, non applicato.
 */
export const saveOutcomeBodySchema = z.strictObject({
    outcome: z.enum(CALL_OUTCOMES),
    answeredOutcome: z.enum(ANSWERED_OUTCOMES).nullable(),
    detail: z.enum(OUTCOME_DETAILS).nullable(),
    note: multiLineTextSchema(4000).nullable(),
    requestId: idSchema,
});
/** La chiamata con l'esito salvato e la nota degli appunti, `null` senza appunti. */
export const saveOutcomeResponseSchema = z.object({ call: callViewSchema, noteId: z.string().nullable() });
/** I dettagli di `409 call_outcome_exists`: l'esito che la chiamata ha già, che resta quello. */
export const callOutcomeExistsDetailsSchema = z.object({ outcome: z.enum(CALL_OUTCOMES), answeredOutcome: z.enum(ANSWERED_OUTCOMES).nullable() });
/** `GET /api/leads/:leadId/calls/open`: la chiamata di chi guarda ancora senza esito su quel Lead, per «Manca l'esito…». */
export const openCallResponseSchema = z.object({ call: callViewSchema.nullable() });
/** `POST /api/telephony/token`: il permesso di comporre del browser, che non si conserva (`Cache-Control: no-store`). */
export const telephonyTokenResponseSchema = z.object({ provider: telephonyProviderSchema, token: z.string(), expiresAt: z.string() });
/** I dettagli di `422 lead_not_callable`: il motivo del Lead o del telefono. */
export const leadNotCallableDetailsSchema = z.object({ reason: notCallableReasonSchema, byDisplayName: z.string().optional() });
/**
 * I dettagli di `409 lead_reserved` (piano M5, D2): il Lead è aperto nel Power Dialer di un collega, che lo tiene riservato;
 * «Lo sta lavorando {nome} adesso».
 */
export const leadReservedDetailsSchema = z.object({ byDisplayName: z.string() });
/**
 * I dettagli di `409 lead_in_call` (questione 163): chi è al telefono con il Lead e se è chi chiede; soltanto allora anche
 * la chiamata, che chi la sta facendo può riprendere o chiudere.
 */
export const leadInCallDetailsSchema = z.object({ byDisplayName: z.string(), mine: z.boolean(), callId: z.string().optional() });
/**
 * `GET /api/calls/:callId/recording` (piano M4, Task 11a, D8): l'indirizzo per ascoltare la registrazione, valido cinque minuti
 * (`expiresAt`), con la durata dell'audio. Dal 4 ottobre 2026, per l'audio che resta presso il fornitore, è un indirizzo di
 * Gioia365 relativo (`/api/recording-audio/<gettone>`); per l'audio copiato prima nello spazio europeo, quello firmato dello
 * spazio. Si chiede a ogni ascolto e non si conserva: la risposta non va in nessuna cache.
 */
export const recordingLinkResponseSchema = z.object({ url: z.string(), expiresAt: z.string(), durationSeconds: z.number().int().nullable() });
//# sourceMappingURL=calls.js.map